import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  FileText, Download, Eye, DollarSign, Users, 
  Briefcase, TrendingUp, Clock, Receipt, MapPin,
  Mail, Phone, Calendar, BarChart3
} from 'lucide-react';

export default function ComprehensiveReports() {
  const [activeTab, setActiveTab] = useState('financial');
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const reportCategories = {
    financial: {
      name: 'Financial Reports',
      icon: DollarSign,
      reports: [
        { 
          id: 'payments-transactions', 
          name: 'Payments Transactions', 
          description: 'All payments, deposits, and refunds processed',
          endpoint: '/reports/payments-transactions'
        },
        { 
          id: 'projected-income', 
          name: 'Projected Income', 
          description: 'Projected income from invoices awaiting payment',
          endpoint: '/reports/projected-income'
        },
        { 
          id: 'transaction-list', 
          name: 'Transaction List', 
          description: 'All transactions from invoices, payments & deposits',
          endpoint: '/reports/transactions'
        },
        { 
          id: 'invoices', 
          name: 'Invoices Report', 
          description: 'Invoices report with additional client data',
          endpoint: '/reports/invoices'
        },
        { 
          id: 'taxation', 
          name: 'Taxation Report', 
          description: 'Tax totals, total awaiting collection, and total by tax rate',
          endpoint: '/reports/taxation'
        },
        { 
          id: 'aged-receivables', 
          name: 'Aged Receivables', 
          description: 'Invoices that are late by 30, 60, and 90+ days',
          endpoint: '/reports/aged-receivables'
        },
        { 
          id: 'bad-debt', 
          name: 'Bad Debt', 
          description: 'All invoices marked as bad debt',
          endpoint: '/reports/bad-debt'
        },
        { 
          id: 'client-balance', 
          name: 'Client Balance Summary', 
          description: 'Full list of customer account balances',
          endpoint: '/reports/client-balance'
        }
      ]
    },
    work: {
      name: 'Work Reports',
      icon: Briefcase,
      reports: [
        { 
          id: 'visits', 
          name: 'Visits Report', 
          description: 'Visits report with additional custom field data',
          endpoint: '/reports/visits'
        },
        { 
          id: 'one-off-jobs', 
          name: 'One-off Jobs', 
          description: 'One-off job report with additional client and job data',
          endpoint: '/reports/one-off-jobs'
        },
        { 
          id: 'recurring-jobs', 
          name: 'Recurring Jobs', 
          description: 'Recurring jobs report with additional client data',
          endpoint: '/reports/recurring-jobs'
        },
        { 
          id: 'requests', 
          name: 'Requests & Assessments', 
          description: 'Requests and assessments report',
          endpoint: '/reports/requests'
        },
        { 
          id: 'job-forms', 
          name: 'Job Forms', 
          description: 'Detailed job forms data',
          endpoint: '/reports/job-forms'
        },
        { 
          id: 'quotes-report', 
          name: 'Quotes Report', 
          description: 'Quotes report with additional client data',
          endpoint: '/reports/quotes-report'
        }
      ]
    },
    team: {
      name: 'Team Productivity',
      icon: Users,
      reports: [
        { 
          id: 'productivity-metrics', 
          name: 'Productivity Metrics', 
          description: 'Productivity metrics for each team member',
          endpoint: '/reports/productivity-metrics'
        },
        { 
          id: 'salesperson-performance', 
          name: 'Salesperson Performance', 
          description: 'Conversion rate information for each salesperson',
          endpoint: '/reports/salesperson-performance'
        },
        { 
          id: 'products-services', 
          name: 'Products & Services Usage', 
          description: 'Full usage of products & services on quotes, jobs, and invoices',
          endpoint: '/reports/products-services'
        },
        { 
          id: 'waypoints', 
          name: 'GPS Waypoints', 
          description: 'Full list of GPS waypoints logged',
          endpoint: '/reports/waypoints'
        },
        { 
          id: 'timesheets', 
          name: 'Timesheets', 
          description: 'All time tracked for the team',
          endpoint: '/reports/timesheets'
        }
      ]
    },
    expenses: {
      name: 'Expense Reports',
      icon: Receipt,
      reports: [
        { 
          id: 'expenses-detailed', 
          name: 'Expenses Report', 
          description: 'All tracked expenses and their details',
          endpoint: '/reports/expenses'
        }
      ]
    },
    clients: {
      name: 'Client Reports',
      icon: Users,
      reports: [
        { 
          id: 'clients-list', 
          name: 'Clients List', 
          description: 'All clients with information about which lead channels they came from',
          endpoint: '/reports/clients'
        },
        { 
          id: 'lead-source', 
          name: 'Lead Source Revenue', 
          description: 'Revenue by lead source',
          endpoint: '/reports/lead-source'
        },
        { 
          id: 'client-communications', 
          name: 'Client Communications', 
          description: 'All messages sent through system, including emails and text messages',
          endpoint: '/reports/client-communications'
        },
        { 
          id: 'job-followup-emails', 
          name: 'Job Follow-up Emails', 
          description: 'All job follow-up emails sent to clients',
          endpoint: '/reports/job-followup-emails'
        },
        { 
          id: 'client-contact-info', 
          name: 'Client Contact Info', 
          description: 'All clients and their contact info',
          endpoint: '/reports/client-contact-info'
        },
        { 
          id: 'property-list', 
          name: 'Property List', 
          description: 'All properties and their details',
          endpoint: '/reports/property-list'
        },
        { 
          id: 'client-reengagement', 
          name: 'Client Re-engagement', 
          description: "Detailed list of clients that haven't had a closed job in the past 12 months",
          endpoint: '/reports/client-reengagement'
        }
      ]
    }
  };

  const fetchReport = async (report) => {
    setLoading(true);
    setSelectedReport(report);
    try {
      const response = await axiosInstance.get(
        `${report.endpoint}?start_date=${dateRange.start}&end_date=${dateRange.end}`
      );
      setReportData(response.data);
      toast.success(`${report.name} loaded successfully`);
    } catch (error) {
      toast.error(`Failed to load ${report.name}`);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).map(val => `"${val}"`).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported to CSV');
  };

  const renderReportCard = (report, category) => (
    <Card 
      key={report.id} 
      className="backdrop-blur-sm bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer"
      data-testid={`report-card-${report.id}`}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText size={18} className="text-[#595959]" />
          {report.name}
        </CardTitle>
        <CardDescription className="text-sm">
          {report.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => fetchReport(report)}
            data-testid={`view-report-${report.id}`}
            className="flex-1"
          >
            <Eye size={14} className="mr-1" />
            View Report
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (reportData.length === 0) {
                toast.error('Please view the report first');
                return;
              }
              exportToCSV(reportData, report.id);
            }}
            data-testid={`export-report-${report.id}`}
          >
            <Download size={14} className="mr-1" />
            Export
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderReportTable = () => {
    if (!selectedReport || reportData.length === 0) {
      return (
        <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Select a report to view data</p>
            <p className="text-gray-400 text-sm">Click "View Report" on any report card</p>
          </CardContent>
        </Card>
      );
    }

    const columns = Object.keys(reportData[0]);

    return (
      <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{selectedReport.name}</CardTitle>
              <CardDescription>
                {reportData.length} records found | {dateRange.start} to {dateRange.end}
              </CardDescription>
            </div>
            <Button
              onClick={() => exportToCSV(reportData, selectedReport.id)}
              data-testid="export-current-report"
            >
              <Download size={16} className="mr-2" />
              Export to CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  {columns.map((col) => (
                    <th key={col} className="p-3 text-left text-sm font-medium text-gray-700 border-b">
                      {col.replace(/_/g, ' ').toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.slice(0, 50).map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 border-b">
                    {columns.map((col) => (
                      <td key={col} className="p-3 text-sm text-gray-600">
                        {typeof row[col] === 'number' && col.includes('amount') 
                          ? `$${row[col].toFixed(2)}` 
                          : String(row[col] || 'N/A')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {reportData.length > 50 && (
              <p className="text-sm text-gray-500 mt-4 text-center">
                Showing first 50 of {reportData.length} records. Export to CSV for full data.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="comprehensive-reports">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Comprehensive Reports
        </h2>
        <p className="text-gray-600">View and export detailed reports across all business segments</p>
      </div>

      {/* Date Range Filter */}
      <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0 mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Date Range Filter</CardTitle>
          <CardDescription>Select date range for all reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                data-testid="report-start-date"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                data-testid="report-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="financial" data-testid="financial-tab">
            <DollarSign size={16} className="mr-2" />
            Financial
          </TabsTrigger>
          <TabsTrigger value="work" data-testid="work-tab">
            <Briefcase size={16} className="mr-2" />
            Work
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="team-tab">
            <Users size={16} className="mr-2" />
            Team
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="expenses-tab">
            <Receipt size={16} className="mr-2" />
            Expenses
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="clients-tab">
            <Users size={16} className="mr-2" />
            Clients
          </TabsTrigger>
        </TabsList>

        {Object.entries(reportCategories).map(([key, category]) => (
          <TabsContent key={key} value={key} className="space-y-6">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <category.icon size={24} className="text-[#595959]" />
                {category.name}
              </h3>
              <p className="text-gray-600">{category.reports.length} reports available</p>
            </div>

            {/* Report Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {category.reports.map(report => renderReportCard(report, category))}
            </div>

            {/* Report Data Table */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
              </div>
            ) : (
              renderReportTable()
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
